import { useEffect, useState } from 'react';
import { SpotLoading, SpotPill, SpotSvg } from '@enterprise/spot';
import { _value, Value } from '../../../components/helpers/Value';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { observer } from 'mobx-react';
import { Trans } from 'react-i18next';
import i18n from 'i18next';
import { InvoiceItemData, InvoiceItemWithVariantDetail } from '../../../core/models/datasource/invoiceItems/InvoiceItemWithVariantDetail';
import { InvoiceItem, InvoiceItemParent } from '../../../core/models/datasource/invoiceItems';
import { ItemsGroupByType } from '../../../core/models/organization/settings/enum';
import { OrganizationSettingsMasterSite } from '../../../core/models/organization/settings';
import { useStores } from '../../../hooks';
import { ItemAddToPracticesParams, ItemAddPractices } from './ItemAddPractice/ItemAddPractices';
import { SiteEntityInfo } from '../../practiceConnections/MyOrganizationPageStore';
import { cloneDeep } from 'lodash';
import { defaultEmptyPriceBreakValues, removeEmptyPriceBreaks } from '../../../core/helpers/validation.helpers';
import { DatasourceFullSiteInfo } from 'apps/client/src/core/models/datasource/sites';

interface ItemCardDetailProps {
    item: InvoiceItemWithVariantDetail;
    onEditClicked?: (item: InvoiceItem) => void;
    masterSite?: OrganizationSettingsMasterSite;
    matchSubLabel: boolean;
}

export const ItemCardDetails = observer(function ItemCardDetailsComponent(props: ItemCardDetailProps) {
    const { item, masterSite, matchSubLabel } = props;
    const {
        domain: { itemsPageStore },
    } = useStores();

    const { isCreatingNewItem, itemsGroupBy, invoiceItem, applications } = itemsPageStore;
    const [showSelectPracticesModal, setShowSelectPracticesModal] = useState(false);
    const [data, setData] = useState<InvoiceItemData>({} as InvoiceItemData);

    useEffect(() => {
        if (isCreatingNewItem) {
            return;
        }

        const isSub = Boolean(item.itemParent);

        if (itemsGroupBy === ItemsGroupByType.Description) {
            void itemsPageStore.fetchInvoiceItem({ description: item.hospitalDescription, isSub });
        }
        void itemsPageStore.fetchInvoiceItem_legacy({ id: item.pimsGeneratedId });
    }, [itemsGroupBy, isCreatingNewItem, item]);

    useEffect(() => {
        if (!invoiceItem?.difference) {
            return;
        }

        setData(cloneDeep(invoiceItem?.difference));
    }, [invoiceItem?.difference]);

    const getSitesWithoutItem = (): string[] => {
        const sitesWithItem = invoiceItem?.sites?.map((site) => site.id) || [];
        const activeSites = applications?.filter((app) => app.isActive);
        if (!activeSites) {
            return [];
        }
        const allSites = activeSites.map((site) => site.id);
        return allSites?.filter((site) => !sitesWithItem.includes(site));
    };

    const filterSelectedPractices = (practices: SiteEntityInfo[], activeSites: DatasourceFullSiteInfo[], masterSiteId): DatasourceFullSiteInfo[] => {
        const newList: DatasourceFullSiteInfo[] = [];
        for (const practice of practices) {
            const match = activeSites.find((site) => {
                return site.id === practice.id;
            });
            if (match) {
                match.provider.siteId = practice.id;
                match.provider.name = practice.pimsName || '';
                match.masterSiteId = masterSiteId;
                newList.push(match);
            }
        }

        return newList;
    };

    const getOriginalItemParent = (pimsGeneratedId?: string): InvoiceItemParent | undefined => {
        if (!pimsGeneratedId) {
            return undefined;
        }

        const { master } = data;
        const variants = invoiceItem?.variants;
        const originalVariant = variants?.find((variant) => variant.pimsGeneratedId === pimsGeneratedId);

        if (!master?.itemParent) {
            return undefined;
        }

        return originalVariant?.itemParent;
    };

    const unpackEntity = (activeSites: DatasourceFullSiteInfo[], copyFromPractice: SiteEntityInfo): Partial<InvoiceItemWithVariantDetail>[] => {
        const variants = invoiceItem?.variants;
        const dataKeys = Object.keys(data);
        const nonMasterData = dataKeys?.find((v) => {
            return v.includes(copyFromPractice.id);
        });
        const entityData = nonMasterData ? { master: data[nonMasterData] } : { master: data.master };

        const items = activeSites
            .map(({ id, provider, masterSiteId }) => {
                const { master } = entityData;

                // pimsGeneratedIds get lost on variants and will default to the master id, so we need to restore them
                const itemVariant = variants?.find((variant) => {
                    return variant.provider.siteId === id;
                });

                const originalItemParent = getOriginalItemParent(itemVariant?.pimsGeneratedId);
                const variant: Partial<InvoiceItemWithVariantDetail> = Object.assign<Partial<InvoiceItem>, Partial<InvoiceItem>>(cloneDeep(master), {
                    provider: { name: master?.provider?.name, siteId: provider.siteId },
                    masterSiteId: masterSiteId,
                });

                if (master.itemParent) {
                    variant.itemParent = originalItemParent;
                }

                return variant;
            })
            .filter((variant) => {
                const { master } = entityData;
                // remove all sub-products variants with does not have parent product before send to api
                return !master.itemParent || variant.itemParent;
            })
            .map(removeEmptyPriceBreaks)
            .map(defaultEmptyPriceBreakValues);

        return items;
    };

    const onSave = (sites: DatasourceFullSiteInfo[], copyFromPractice: SiteEntityInfo, runAt?: Date, name?: string): Promise<void> => {
        const variants = unpackEntity(sites, copyFromPractice);
        const isScheduledSave = Boolean(runAt);
        const message = itemsPageStore.getAddItemToPracticesMessage({ practiceCount: variants.length, isScheduledUpdate: isScheduledSave });

        if (isScheduledSave) {
            return itemsPageStore.scheduleSave(variants, runAt, message, name);
        } else {
            return itemsPageStore.save(variants, message);
        }
    };

    const onSubmitSelectedPractices = ({ originPractice, targetPractices, runAt, name }: ItemAddToPracticesParams) => {
        const activeSites = applications.filter((app) => app.isActive);
        const newList = filterSelectedPractices(targetPractices as SiteEntityInfo[], activeSites, originPractice.id);
        setShowSelectPracticesModal(false);
        onSave(newList, originPractice as SiteEntityInfo, runAt, name);
    };

    const sitesWithoutItem = getSitesWithoutItem();

    return (
        <>
            {!invoiceItem && <SpotLoading />}
            {invoiceItem && (
                <Container className="item-card-details" fluid={true}>
                    <Row noGutters={true} className={'practice-pill-container'}>
                        <Col xs={2} className={'practice-pill-buttons'}>
                            <SpotPill className={'practice-pill-button-text'}>
                                <Trans i18nKey="controlCenter:itemSearch.practices" count={invoiceItem.sites?.length}>
                                    {invoiceItem.sites?.length} hospital(s)
                                </Trans>
                            </SpotPill>
                        </Col>
                        <Col xs={2} className={'practice-pill-buttons'}>
                            <SpotPill type="warning" className={'practice-pill-button-text'}>
                                <Trans i18nKey="controlCenter:itemSearch.variants" count={Object.keys(invoiceItem.difference || {}).length - 1}>
                                    {Object.keys(invoiceItem.difference || {}).length - 1} variant(s)
                                </Trans>
                            </SpotPill>
                        </Col>
                        {sitesWithoutItem.length > 0 && !matchSubLabel && (
                            <Col xs={3}>
                                <div className={'add-additional-clinics'}>
                                    <a
                                        onClick={() => {
                                            setShowSelectPracticesModal(true);
                                        }}
                                        className={'spot-link'}
                                        href={'javascript:void(0)'}
                                        data-automation-id="add-additional-practices-button"
                                    >
                                        <SpotSvg icon="add" className={'clinic-list-add-icon'} />
                                        <span>{i18n.t('controlCenter:itemSelectPractices:addToPractices', 'Add to Additional Practice(s)')}</span>
                                    </a>
                                </div>
                            </Col>
                        )}
                    </Row>
                    <Row noGutters={true}>
                        <Col xs={12}>
                            <div className="d-flex flex-row flex-wrap gap-2">
                                {Array.from(invoiceItem.variants)
                                    .sort(
                                        (a, b) =>
                                            _value(a, 'prices.priceBreaks[0].invoicePrice', 0) - _value(b, 'prices.priceBreaks[0].invoicePrice', 0),
                                    )
                                    .sort((a) => (masterSite?.siteId === a.provider.siteId.toString() ? -1 : 1))
                                    .map((variant: InvoiceItem) => (
                                        <div key={`${variant.provider.siteId}-${variant.pimsGeneratedId}`} className="card-holder">
                                            <p>
                                                <strong>
                                                    <Trans i18nKey="common:price"> Price($)</Trans>:{' '}
                                                    <Value value={variant} path="prices.priceBreaks[0].invoicePrice" defaultValue="0" />
                                                </strong>
                                            </p>
                                            <Card className="app-info ">
                                                <div className="site-name">
                                                    {
                                                        (
                                                            invoiceItem.sites?.find((site) => String(site.id) === variant.provider.siteId) || {
                                                                name: i18n.t('common:unknown', 'Unknown'),
                                                            }
                                                        ).name
                                                    }
                                                </div>
                                                <div className="status">
                                                    <small>
                                                        {variant.isActive ? i18n.t('common:active', 'Active') : i18n.t('common:inactive', 'Inactive')}
                                                    </small>
                                                </div>
                                            </Card>
                                        </div>
                                    ))}
                            </div>
                        </Col>
                    </Row>

                    {sitesWithoutItem.length > 0 && matchSubLabel && (
                        <div>
                            <Row noGutters={true} className={'subProductMessage'}>
                                <span className={'text'}>
                                    <Trans i18nKey="common:item.addToRemainingSubProductText" />
                                </span>
                                <span className={'subtext'}>
                                    <Trans i18nKey="common:item.addToRemainingSubProductSubtext" />
                                </span>
                            </Row>
                        </div>
                    )}
                </Container>
            )}
            {showSelectPracticesModal && (
                <ItemAddPractices
                    sitesWithoutItems={sitesWithoutItem}
                    title={i18n.t('controlCenter:itemSelectPractices:addToAdditionalPractices', 'Add this item to Additional Practices')}
                    secondaryTitle={item.hospitalDescription}
                    onSubmit={onSubmitSelectedPractices}
                    onClose={() => {
                        setShowSelectPracticesModal(false);
                    }}
                />
            )}
        </>
    );
});
